import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formaterDate, formaterMontantDevise } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { BarreFiltres, FiltreChoix } from '../composants/Filtres';
import { Nombre } from '../composants/Nombre';
import { LienFiche } from '../composants/LienAction';
import { Note } from '../composants/Note';
import { Pastille, type TonPastille } from '../composants/Pastille';

type EtatInvestissement =
  | 'A_PLACER'
  | 'ORDRES_EN_COURS'
  | 'ORDRES_TRANSMIS'
  | 'RELIQUAT'
  | 'SORTIE_OUVERTE'
  | 'SOUS_SEUIL'
  | 'DANS_TAMPON'
  | 'SANS_SITUATION';

/**
 * Un mandat actif et son état face à l'investissement, tel que le serveur le
 * décide. Sa règle tient compte du solde **projeté** à la dernière situation
 * enregistrée, des ordres déjà en cours, des sorties ouvertes et des seuils du
 * moteur — que l'écran ne saurait reproduire fidèlement, et n'a pas à
 * reproduire : le compteur de l'accueil lit la même source.
 *
 * Sans situation, les montants sont absents.
 */
interface LigneAInvestir {
  mandatId: string;
  reference: string;
  client: string;
  profilId: string;
  profil: string;
  etat: EtatInvestissement;
  /** Le motif complet du serveur, quand l'état en a un. */
  motif: string | null;
  dateSituation: string | null;
  soldeProjete: string | null;
  tamponCible: string | null;
  montantInvestissable: string | null;
  seuil: string | null;
}

const LIBELLE_ETAT: Record<EtatInvestissement, string> = {
  A_PLACER: 'À placer',
  ORDRES_EN_COURS: 'Ordres en cours',
  ORDRES_TRANSMIS: 'Ordres transmis',
  RELIQUAT: 'Reliquat ouvert',
  SORTIE_OUVERTE: 'Sortie ouverte',
  SOUS_SEUIL: 'Sous le seuil',
  DANS_TAMPON: 'Dans le tampon',
  SANS_SITUATION: 'Sans situation',
};

// Ambre : une décision attend le gérant ; Majorelle : des ordres circulent ;
// neutre : rien à faire. Jamais de vert — il est réservé à la performance.
const TON_ETAT: Record<EtatInvestissement, TonPastille> = {
  A_PLACER: 'attente',
  SORTIE_OUVERTE: 'attente',
  ORDRES_EN_COURS: 'interface',
  ORDRES_TRANSMIS: 'interface',
  RELIQUAT: 'interface',
  SOUS_SEUIL: 'neutre',
  DANS_TAMPON: 'neutre',
  SANS_SITUATION: 'neutre',
};

/** Ce qui débloquerait le mandat, en quelques mots ; le motif entier au survol. */
function suite(l: LigneAInvestir): string {
  switch (l.etat) {
    case 'A_PLACER':
      return 'Proposer ou investir en campagne';
    case 'ORDRES_EN_COURS':
      return 'Valider, transmettre ou annuler les ordres';
    case 'ORDRES_TRANSMIS':
      return 'Attendre la situation qui les dénoue';
    case 'RELIQUAT':
      return 'Attendre la situation ou clôturer le panier';
    case 'SORTIE_OUVERTE':
      return 'Servir la sortie de fonds';
    case 'SOUS_SEUIL':
      return `Seuil du moteur : ${formaterMontantDevise(l.seuil ?? '0')}`;
    case 'DANS_TAMPON':
      return '—';
    case 'SANS_SITUATION':
      return 'Importer une situation';
  }
}

/** Où agir, selon l'état. */
function lienAction(l: LigneAInvestir) {
  switch (l.etat) {
    case 'A_PLACER':
      return <LienFiche to={`/mandats/${l.mandatId}/proposition`}>Proposer des ordres</LienFiche>;
    case 'ORDRES_EN_COURS':
    case 'ORDRES_TRANSMIS':
    case 'RELIQUAT':
      return <LienFiche to={`/ordres?mandatId=${l.mandatId}`}>Voir les ordres</LienFiche>;
    case 'SORTIE_OUVERTE':
      return <LienFiche to={`/sorties-fonds?mandatId=${l.mandatId}`}>Voir la sortie</LienFiche>;
    default:
      return null;
  }
}

const OPTIONS_ETAT = Object.entries(LIBELLE_ETAT) as [EtatInvestissement, string][];

const montantOuTiret = (valeur: string | null) =>
  valeur === null ? '—' : <Nombre valeur={valeur} />;

/**
 * Tous les mandats actifs et leur état face à l'investissement, et
 * l'investissement de plusieurs mandats d'un geste.
 *
 * Un mandat absent de la file laissait le gérant deviner : trésorerie placée,
 * ou ordres en attente ? Chaque mandat y figure désormais, avec ce qui le
 * bloque (demande du 14/10/2026).
 *
 * Le lot passe par une **campagne d'investissement**, comme le rebalancing :
 * récapitulatif par mandat, exclusions, validation globale (précision du
 * 13/10/2026). Seuls les mandats « à placer » s'y sélectionnent.
 *
 * Les filtres sont des **vues** sur la file, pas une seconde règle : le
 * nombre de mandats « à placer » est exactement celui de l'accueil.
 */
export function AInvestir() {
  const { aRole } = useAuth();
  const naviguer = useNavigate();
  const peutGerer = aRole('GERANT', 'RESP_GESTION');

  const file = useRessource<LigneAInvestir[]>('/portefeuilles/a-investir');
  const toutes = file.donnees ?? [];

  const [profil, setProfil] = useState('');
  const [etat, setEtat] = useState('');
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [occupe, setOccupe] = useState(false);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  const profils = [...new Map(toutes.map((l) => [l.profilId, l.profil])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1], 'fr'),
  );
  const lignes = toutes.filter(
    (l) => (!profil || l.profilId === profil) && (!etat || l.etat === etat),
  );
  const nbAPlacer = toutes.filter((l) => l.etat === 'A_PLACER').length;

  // Seuls les mandats visibles et plaçables comptent : changer de filtre ne
  // doit pas laisser dans le lot des mandats que le gérant ne voit plus.
  const selectionnables = lignes.filter((l) => l.etat === 'A_PLACER');
  const choisis = selectionnables.filter((l) => selection.has(l.mandatId));
  const toutSelectionne = selectionnables.length > 0 && choisis.length === selectionnables.length;
  const partiellement = choisis.length > 0 && !toutSelectionne;

  function basculer(id: string) {
    const copie = new Set(selection);
    if (copie.has(id)) copie.delete(id);
    else copie.add(id);
    setSelection(copie);
  }

  function basculerTout() {
    setSelection(toutSelectionne ? new Set() : new Set(selectionnables.map((l) => l.mandatId)));
  }

  function filtrerProfil(valeur: string) {
    setSelection(new Set());
    setProfil(valeur);
  }

  function filtrerEtat(valeur: string) {
    setSelection(new Set());
    setEtat(valeur);
  }

  function retirerFiltres() {
    setSelection(new Set());
    setProfil('');
    setEtat('');
  }

  async function investir() {
    setErreurAction(null);
    setOccupe(true);
    try {
      const c = await api.post<{ id: string }>('/campagnes/investissement', {
        mandatIds: choisis.map((l) => l.mandatId),
      });
      naviguer(`/campagnes/${c.id}`);
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Génération impossible.');
      setOccupe(false);
    }
  }

  const colonnes: Colonne<LigneAInvestir>[] = [
    ...(peutGerer
      ? [
          {
            cle: 'choix',
            entete: (
              <input
                type="checkbox"
                checked={toutSelectionne}
                // L'état indéterminé ne s'exprime pas en JSX : il se pose sur le nœud.
                ref={(noeud) => {
                  if (noeud) noeud.indeterminate = partiellement;
                }}
                onChange={basculerTout}
                disabled={selectionnables.length === 0}
                aria-label="Tout sélectionner"
              />
            ),
            rendu: (l: LigneAInvestir) =>
              l.etat === 'A_PLACER' ? (
                <input
                  type="checkbox"
                  checked={selection.has(l.mandatId)}
                  onChange={() => basculer(l.mandatId)}
                  aria-label={`Sélectionner ${l.reference}`}
                />
              ) : null,
          },
        ]
      : []),
    {
      cle: 'mandat',
      entete: 'Mandat',
      num: true,
      rendu: (l) => <LienFiche to={`/mandats/${l.mandatId}/tresorerie`}>{l.reference}</LienFiche>,
    },
    { cle: 'client', entete: 'Client', rendu: (l) => l.client },
    { cle: 'profil', entete: 'Profil', rendu: (l) => l.profil },
    {
      cle: 'etat',
      entete: 'État',
      rendu: (l) => <Pastille ton={TON_ETAT[l.etat]}>{LIBELLE_ETAT[l.etat]}</Pastille>,
    },
    {
      cle: 'suite',
      entete: 'Suite à donner',
      rendu: (l) => <span title={l.motif ?? undefined}>{suite(l)}</span>,
    },
    {
      cle: 'situation',
      entete: 'Situation au',
      num: true,
      rendu: (l) => (l.dateSituation ? formaterDate(l.dateSituation) : '—'),
    },
    {
      cle: 'projete',
      entete: 'Solde projeté',
      num: true,
      rendu: (l) => montantOuTiret(l.soldeProjete),
    },
    { cle: 'cible', entete: 'Tampon', num: true, rendu: (l) => montantOuTiret(l.tamponCible) },
    {
      cle: 'placer',
      entete: 'Excédent',
      num: true,
      rendu: (l) => montantOuTiret(l.montantInvestissable),
    },
    { cle: 'action', entete: '', num: true, rendu: lienAction },
  ];

  return (
    <>
      <p className="entete__fil">Gestion</p>
      <div className="entete">
        <h1>À investir</h1>
        <span className="entete__ref">
          {`${nbAPlacer} à placer sur ${toutes.length} mandat(s) actif(s)`}
        </span>
      </div>

      {file.erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(file.erreur)}
        </BandeauAlerte>
      )}
      {erreurAction && (
        <BandeauAlerte nature="refus" titre="Génération refusée">
          {erreurAction}
        </BandeauAlerte>
      )}

      <Bloc
        titre="Mandats actifs"
        meta={
          file.chargement
            ? 'chargement…'
            : profil || etat
              ? `${lignes.length} affiché(s)`
              : 'à placer en tête, du plus gros disponible'
        }
      >
        <BarreFiltres actifs={profil !== '' || etat !== ''} onRetirer={retirerFiltres}>
          <FiltreChoix
            libelle="Profil"
            tous="Tous les profils"
            valeur={profil}
            onChange={filtrerProfil}
            options={profils}
          />
          <FiltreChoix
            libelle="État"
            tous="Tous les états"
            valeur={etat}
            onChange={filtrerEtat}
            options={OPTIONS_ETAT}
          />
        </BarreFiltres>

        <Tableau
          colonnes={colonnes}
          lignes={lignes}
          cle={(l) => l.mandatId}
          vide={profil || etat ? 'Aucun mandat ne répond à ces filtres.' : 'Aucun mandat actif.'}
        />
        <Note resume="Un mandat est à placer quand sa trésorerie excède le tampon au-delà du seuil du moteur, sans ordre ni sortie de fonds en cours.">
          La file part de la dernière situation de chaque mandat, ordres transmis depuis et sorties
          ouvertes déduits. Des ordres non dénoués bloquent le mandat : le moteur ne les voit pas
          dans la situation et proposerait une seconde fois les mêmes opérations. Une sortie de
          fonds ouverte se sert depuis l’écran Sorties de fonds, jamais par campagne. Le motif
          complet de chaque état s’affiche au survol de la colonne « Suite à donner ».
        </Note>

        {peutGerer && (
          <div className="barre-actions barre-actions--apres">
            <button
              className="bouton"
              disabled={occupe || choisis.length === 0}
              onClick={() => void investir()}
            >
              {occupe ? 'Génération…' : `Investir en campagne (${choisis.length})`}
            </button>
          </div>
        )}
      </Bloc>
    </>
  );
}
